// src/pages/KanbanPlanSheet/KanbanPlanSheetPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Form,
  Select,
  Button,
  Table,
  Input,
  Space,
  message,
  Row,
  Col,
  Typography,
  Modal,
  InputNumber,
  Tag,
  Grid,
  Alert,
  Spin,
} from "antd";
import {
  SendOutlined,
  ReloadOutlined,
  SearchOutlined,
  CopyOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FilterOutlined,
  EditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";

import {
  getActiveItems,
  getStyleNumbers,
  getPONumbers,
  getColors,
  getKanbanDetailsForSorting,
  updateSizeQuantity,
  setSortingOne,
} from "../../services/KanbanPlanSheetService";

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const KanbanPlanSheetPage = ({ userdata }) => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const screens = useBreakpoint();

  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);

  // dropdown loaders
  const [itemLoading, setItemLoading] = useState(false);
  const [styleLoading, setStyleLoading] = useState(false);
  const [poLoading, setPoLoading] = useState(false);
  const [colorLoading, setColorLoading] = useState(false);

  // Dropdown data states
  const [itemNumbers, setItemNumbers] = useState([]);
  const [styleNumbers, setStyleNumbers] = useState([]);
  const [poNumbers, setPONumbers] = useState([]);
  const [colors, setColors] = useState([]);

  // store response.full_data => [{set_id,color}]
  const [colorSetMap, setColorSetMap] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState(null);

  // Selected values
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);

  // Table data
  const [tableData, setTableData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [kanbanInfo, setKanbanInfo] = useState(null);
  const [sizeColumns, setSizeColumns] = useState([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editingSizeKey, setEditingSizeKey] = useState(null);
  const [poDetails, setPoDetails] = useState(null);
  const [kbTotals, setKbTotals] = useState(null);
  const [preparedSizeTotals, setPreparedSizeTotals] = useState(null);

  const ROW_H = 54;
  const HEADER_H = 56;
  const SUMMARY_H = 56;
  const MIN_ROWS = 10;
  const minTableY = HEADER_H + SUMMARY_H + MIN_ROWS * ROW_H;
  const tableY = Math.max(minTableY, window.innerHeight - 400);

  useEffect(() => {
    loadItemNumbers();
  }, []);

  const dropdownSpinner = <Spin size="small" style={{ display: "block", padding: 8 }} />;

  const loadItemNumbers = async () => {
    try {
      setItemLoading(true);
      const response = await getActiveItems();
      if (response?.status) {
        setItemNumbers(response.data || []);
      } else {
        message.error(response?.message || "Failed to load item numbers");
      }
    } catch (error) {
      message.error("Error loading item numbers");
      console.error("Error loading item numbers:", error);
    } finally {
      setItemLoading(false);
    }
  };

  const handleItemChange = async (value) => {
    setSelectedItem(value || null);
    setSelectedStyle(null);
    setSelectedPO(null);
    setSelectedColor(null);
    setSelectedSetId(null);

    setStyleNumbers([]);
    setPONumbers([]);
    setColors([]);
    setColorSetMap([]);

    form.setFieldsValue({
      itemNo: value || null,
      style: null,
      po: null,
      color: null,
      setId: null,
    });

    if (!value) return;

    try {
      setStyleLoading(true);
      const response = await getStyleNumbers(value);
      if (response?.status) {
        setStyleNumbers(response.style_numbers || []);
      } else {
        message.error(response?.message || "Failed to load style numbers");
      }
    } catch (error) {
      message.error("Error loading style numbers");
      console.error("Error loading style numbers:", error);
    } finally {
      setStyleLoading(false);
    }
  };

  const handleStyleChange = async (value) => {
    setSelectedStyle(value || null);
    setSelectedPO(null);
    setSelectedColor(null);
    setSelectedSetId(null);

    setPONumbers([]);
    setColors([]);
    setColorSetMap([]);

    form.setFieldsValue({
      style: value || null,
      po: null,
      color: null,
      setId: null,
    });

    if (!value) return;

    try {
      setPoLoading(true);
      const response = await getPONumbers(value);
      if (response?.status) {
        setPONumbers(response.po_numbers || []);
      } else {
        message.error(response?.message || "Failed to load PO numbers");
      }
    } catch (error) {
      message.error("Error loading PO numbers");
      console.error("Error loading PO numbers:", error);
    } finally {
      setPoLoading(false);
    }
  };

  const handlePOChange = async (value) => {
    setSelectedPO(value || null);
    setSelectedColor(null);
    setSelectedSetId(null);

    setColors([]);
    setColorSetMap([]);

    form.setFieldsValue({
      po: value || null,
      color: null,
      setId: null,
    });

    if (!value) return;

    try {
      setColorLoading(true);
      const response = await getColors(value);

      if (response?.status) {
        setColors(response.colors || []);
        setColorSetMap(response.full_data || []);
      } else {
        message.error(response?.message || "Failed to load colors");
      }
    } catch (error) {
      message.error("Error loading colors");
      console.error("Error loading colors:", error);
    } finally {
      setColorLoading(false);
    }
  };
const handleOpenEditModal = (record, sizeKey) => {
  const dept2Locked = ["active", "cleared"].includes(record.dept2_status?.status);
  const dept3Locked = ["active", "cleared"].includes(record.dept3_status?.status);

  const isLocked = dept2Locked || dept3Locked;

  let lockMessage = "";

  if (dept2Locked && dept3Locked) {
    lockMessage = "Kanban in Supermarket / Kanban in Sewing Line";
  } else if (dept2Locked) {
    lockMessage = "Kanban in Supermarket";
  } else if (dept3Locked) {
    lockMessage = "Kanban in Sewing Line";
  }

  record._isLocked = isLocked;
  record._lockMessage = lockMessage;

  setEditingRecord(record);
  setEditingSizeKey(sizeKey);
  setEditModalVisible(true);

  editForm.setFieldsValue({
    [sizeKey]: record[sizeKey] || 0,
  });
};
  const handleCloseEditModal = () => {
    setEditModalVisible(false);
    setEditingRecord(null);
    setEditingSizeKey(null);
    editForm.resetFields();
  };

  const handleSaveSizeQuantity = async () => {
    try {
      const formValues = editForm.getFieldsValue();
      const newValue = Number(formValues[editingSizeKey]);

      const maxAllowed = Number(editingRecord?.[editingSizeKey] || 0);

      if (newValue > maxAllowed) {
        message.error(`Input quantity is greater than real quantity (Max: ${maxAllowed})`);
        return;
      }

      if (newValue < 0) {
        message.error("Quantity cannot be negative");
        return;
      }

      const sizeDataForAPI = {};
      sizeColumns.forEach((size) => {
        sizeDataForAPI[size] =
          size === editingSizeKey ? newValue || "" : editingRecord?.[size] || "";
      });

      setLoading(true);
      const response = await updateSizeQuantity(editingRecord.key, sizeDataForAPI);

      if (response?.status) {
        setTableData((prev) =>
          prev.map((item) =>
            item.key === editingRecord.key
              ? {
                  ...item,
                  [editingSizeKey]: newValue,
                  total: response.total_quantity || item.total,
                }
              : item
          )
        );
        message.success(`${editingSizeKey} quantity updated successfully`);
        handleCloseEditModal();
      } else {
        message.error(response?.message || "Failed to update size quantity");
      }
    } catch (error) {
      console.error(error);
      message.error("Error updating size quantity");
    } finally {
      setLoading(false);
    }
  };

  const sizeOrder = [
    "XXS",
    "XS",
    "S",
    "M",
    "L",
    "XL",
    "2XL",
    "XXL",
    "3XL",
    "XXXL",
    "4XL",
    "XXXXL",
    "5XL",
  ];

  const sortSizes = (a, b) => {
    const indexA = sizeOrder.indexOf(String(a).toUpperCase());
    const indexB = sizeOrder.indexOf(String(b).toUpperCase());

    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    const numA = parseFloat(a);
    const numB = parseFloat(b);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;

    return String(a).localeCompare(String(b));
  };

  const handleLoadKanbanPlan = async () => {
    try {
      await form.validateFields(["itemNo", "style", "po", "setId"]);
      const formValues = form.getFieldsValue();

      const setIdToSend = formValues.setId || selectedSetId;

      if (!setIdToSend) {
        message.error("set_id not found for selected color. Please re-select PO/Color.");
        return;
      }

      setTableLoading(true);

      const response = await getKanbanDetailsForSorting({
        itemNo: formValues.itemNo,
        styleNo: formValues.style,
        poNo: formValues.po,
        color: formValues.color,
        setId: setIdToSend,
      });

      if (response?.status && response?.data?.matched_records) {
        setKanbanInfo({
          setId: response.data.set_id,
          kbNumber: response.data.kb_number,
          totalRecords: response.data.matched_records.length,
        });

        setPoDetails({
          poNo: response.po_table_data?.po_no || "",
          poDate: response.po_table_data?.po_date || "",
          customer: response.po_table_data?.customer || "",
          country: response.po_table_data?.country || "",
          shipDate: response.po_table_data?.ship_date || "",
          cartonPackType: response.carton_pack_type || "",
          kanbanSetSizes: response.kanban_set_sizes || {},
          kanbanSetShipSize: response.kanban_set_ship_size_qty || {},
          orderSizes:
            response.sizes ||
            response.kanban_set_order_size ||
            response.order_sizes ||
            response.order_size ||
            {},
          shipPercentage: response.ship_percentage || {},
        });

        setKbTotals(response.kb_totals || null);
        setPreparedSizeTotals(response.prepared_size_totals || null);

        const allSizeKeys = new Set();
        response.data.matched_records.forEach((record) => {
          try {
            const sizeData = JSON.parse(record.size_name || "{}");
            Object.keys(sizeData).forEach((key) => allSizeKeys.add(key));
          } catch (e) {
            console.warn("Error parsing size_name:", record.size_name);
          }
        });

        const sortedSizeKeys = Array.from(allSizeKeys).sort(sortSizes);
        setSizeColumns(sortedSizeKeys);

        const processedData = response.data.matched_records.map((record, index) => {
          let sizeData = {};
          try {
            sizeData = JSON.parse(record.size_name || "{}");
          } catch (e) {
            console.warn("Error parsing size_name:", record.size_name);
            sizeData = {};
          }

          const total = Object.values(sizeData).reduce((sum, qty) => {
            const numQty = parseInt(qty, 10) || 0;
            return sum + numQty;
          }, 0);

          const dynamicSizeData = {};
          sortedSizeKeys.forEach((sizeKey) => {
            dynamicSizeData[sizeKey] = sizeData[sizeKey] || "";
          });

          return {
            key: record.kb_id,
            sno: index + 1,
            kanbanNumber: record.kb_number,
            poNumber: record.po_no,
            sorting: record.sorting ?? null,
            ...dynamicSizeData,
            total: total || record.quantity,
            lineNumber: "LINE B1",
            sortingDateTime: record.sorting_date_time || null,
            dept2_status: record.dept2_status || null,
            dept3_status: record.dept3_status || null,
          };
        });

        setTableData(processedData);
        message.success(
          `Kanban Plan Sheet loaded successfully - ${processedData.length} records found`
        );
      } else {
        message.error(response?.message || "No data found for the selected criteria");
        setTableData([]);
        setKanbanInfo(null);
        setPoDetails(null);
        setKbTotals(null);
        setPreparedSizeTotals(null);
        setSizeColumns([]);
      }
    } catch (error) {
      if (error?.errorFields) {
        message.warning("Please fill in all required fields");
      } else {
        message.error("Error loading Kanban Plan Sheet");
        console.error("Error loading Kanban Plan Sheet:", error);
        setTableData([]);
        setKanbanInfo(null);
        setPoDetails(null);
        setKbTotals(null);
        setPreparedSizeTotals(null);
        setSizeColumns([]);
      }
    } finally {
      setTableLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();

    setSelectedItem(null);
    setSelectedStyle(null);
    setSelectedPO(null);
    setSelectedColor(null);
    setSelectedSetId(null);

    setStyleNumbers([]);
    setPONumbers([]);
    setColors([]);
    setColorSetMap([]);

    setTableData([]);
    setSearchText("");
    setKanbanInfo(null);
    setPoDetails(null);
    setKbTotals(null);
    setPreparedSizeTotals(null);
    setSizeColumns([]);

    setEditModalVisible(false);
    setEditingRecord(null);
    setEditingSizeKey(null);
    editForm.resetFields();
  };

  const handleSearch = (value) => setSearchText(value);

  const filteredData = useMemo(() => {
    const s = searchText.trim().toLowerCase();
    if (!s) return tableData;

    return tableData.filter((item) =>
      Object.values(item).some((value) => value && String(value).toLowerCase().includes(s))
    );
  }, [tableData, searchText]);

  const handleCopyToClipboard = () => {
    try {
      const headers = [
        "SNo",
        "Ready",
        "Kanban Number",
        "PO Number",
        ...sizeColumns,
        "Total",
        "Line Number",
        "Sorting Date Time",
      ];

      const csvContent = [
        headers.join("\t"),
        ...filteredData.map((row) =>
          [
            row.sno,
            row.sorting !== null && row.sorting !== undefined ? "Prepared" : "Pending",
            row.kanbanNumber,
            row.poNumber,
            ...sizeColumns.map((size) => row[size] || "0"),
            row.total,
            row.lineNumber,
            row.sortingDateTime || "",
          ].join("\t")
        ),
      ].join("\n");

      navigator.clipboard
        .writeText(csvContent)
        .then(() => message.success("Data copied to clipboard"))
        .catch(() => {
          const textArea = document.createElement("textarea");
          textArea.value = csvContent;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
          message.success("Data copied to clipboard");
        });
    } catch (error) {
      message.error("Failed to copy data");
      console.error("Copy error:", error);
    }
  };

  const handleExcelExport = async () => {
    try {
      if (!filteredData.length) return message.warning("No data to export");
      const XLSX = await import("xlsx");

      const headers = [
        "SNo",
        "Ready",
        "Kanban Number",
        "PO Number",
        ...sizeColumns,
        "Total",
        "Line Number",
        "Sorting Date Time",
      ];

      const now = new Date();
      const dateStr = now.toISOString().split("T")[0];

      const metaRows = [
        ["Kanban Plan Sheet"],
        [`Generated on: ${dateStr}`],
        [],
        ["Item No", selectedItem || ""],
        ["Style No", selectedStyle || ""],
        ["PO No", selectedPO || ""],
        ["Color", selectedColor || ""],
        ["Set ID", selectedSetId || ""],
        [],
        ["PO No", poDetails?.poNo || ""],
        ["PO Date", poDetails?.poDate || ""],
        ["Customer", poDetails?.customer || ""],
        ["Country", poDetails?.country || ""],
        ["Ship Date", poDetails?.shipDate || ""],
        ["Carton Pack Type", poDetails?.cartonPackType || ""],
        [],
      ];

      const bodyRows = filteredData.map((row) => [
        row.sno,
        row.sorting !== null && row.sorting !== undefined ? "Prepared" : "Pending",
        row.kanbanNumber,
        row.poNumber,
        ...sizeColumns.map((size) => row[size] || "0"),
        row.total,
        row.lineNumber,
        row.sortingDateTime || "",
      ]);

      const sizeSums = sizeColumns.map((size) =>
        filteredData.reduce((acc, row) => acc + (parseInt(row[size], 10) || 0), 0)
      );
      const totalOfSizes = sizeSums.reduce((s, v) => s + v, 0);
      const summaryRow = ["", "", "Size Summary", "", ...sizeSums, totalOfSizes, "", ""];

      const preparedQtyRow = preparedSizeTotals
        ? [
            "",
            "",
            "Prepared Qty",
            "",
            ...sizeColumns.map((size) => parseInt(preparedSizeTotals[size] || 0, 10)),
            sizeColumns.reduce(
              (sum, size) => sum + (parseInt(preparedSizeTotals[size] || 0, 10) || 0),
              0
            ),
            "",
            "",
          ]
        : null;

      const shipPercentageRow = poDetails?.shipPercentage
        ? [
            "",
            "",
            "Ship %",
            "",
            ...sizeColumns.map((size) => poDetails?.shipPercentage?.[size] || "0%"),
            poDetails?.shipPercentage?.["Total %"] || "0%",
            "",
            "",
          ]
        : null;

      const excelData = [
        ...metaRows,
        headers,
        ...bodyRows,
        [],
        summaryRow,
        ...(preparedQtyRow ? [preparedQtyRow] : []),
        ...(shipPercentageRow ? [shipPercentageRow] : []),
      ];

      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Kanban Plan Sheet");

      const filename = `Kanban_Plan_Sheet_${dateStr}.xlsx`;
      XLSX.writeFile(wb, filename);
      message.success("Excel file downloaded successfully");
    } catch (error) {
      message.error("Failed to export Excel file");
      console.error("Excel export error:", error);
    }
  };

  const handlePDFExport = async () => {
    try {
      if (!filteredData.length) return message.warning("No data to export");

      const { jsPDF } = await import("jspdf");
      await import("jspdf-autotable");

      const doc = new jsPDF("landscape");

      doc.setFontSize(16);
      doc.text("Kanban Plan Sheet", 14, 15);

      doc.setFontSize(10);
      const now = new Date();
      const generatedDate = now.toLocaleDateString();
      doc.text(`Generated on: ${generatedDate}`, 14, 25);

      const metaLines = [
        `Item No: ${selectedItem || ""}`,
        `Style No: ${selectedStyle || ""}`,
        `PO No: ${selectedPO || ""}`,
        `Color: ${selectedColor || ""}`,
        `Set ID: ${selectedSetId || ""}`,
        ``,
        `PO No: ${poDetails?.poNo || ""}`,
        `PO Date: ${poDetails?.poDate || ""}`,
        `Customer: ${poDetails?.customer || ""}`,
        `Country: ${poDetails?.country || ""}`,
        `Ship Date: ${poDetails?.shipDate || ""}`,
        `Carton Pack Type: ${poDetails?.cartonPackType || ""}`,
      ];

      let currentY = 30;
      doc.setFontSize(10);
      metaLines.forEach((line) => {
        doc.text(line, 14, currentY);
        currentY += 6;
      });

      const headers = [
        "SNo",
        "Ready",
        "Kanban Number",
        "PO Number",
        ...sizeColumns,
        "Total",
        "Line Number",
        "Sorting Date Time",
      ];

      const tableDataPDF = filteredData.map((row) => [
        row.sno,
        row.sorting !== null && row.sorting !== undefined ? "Prepared" : "Pending",
        row.kanbanNumber,
        row.poNumber,
        ...sizeColumns.map((size) => row[size] || "0"),
        row.total,
        row.lineNumber,
        row.sortingDateTime || "",
      ]);

      doc.autoTable({
        head: [headers],
        body: tableDataPDF,
        startY: currentY + 4,
        styles: { fontSize: 8 },
        margin: { left: 14, right: 14 },
      });

      const finalY =
        doc.lastAutoTable && doc.lastAutoTable.finalY
          ? doc.lastAutoTable.finalY
          : currentY + 14;

      const sizeSumsPDF = sizeColumns.map((size) =>
        filteredData.reduce((acc, row) => acc + (parseInt(row[size], 10) || 0), 0)
      );
      const totalOfSizesPDF = sizeSumsPDF.reduce((s, v) => s + v, 0);

      const sizeBody = sizeColumns.map((s, idx) => [s, sizeSumsPDF[idx]]);
      sizeBody.push(["Total", totalOfSizesPDF]);

      doc.autoTable({
        head: [["Size", "Qty"]],
        body: sizeBody,
        startY: finalY + 8,
        styles: { fontSize: 10 },
        margin: { left: 14 },
      });

      const fileDate = now.toISOString().split("T")[0];
      const filename = `Kanban_Plan_Sheet_${fileDate}.pdf`;
      doc.save(filename);

      message.success("PDF file downloaded successfully");
    } catch (error) {
      message.error("Failed to export PDF file");
      console.error("PDF export error:", error);
    }
  };

  const handleExport = (type) => {
    if (!tableData.length) return message.warning("No data to export");

    if (type === "Copy") return handleCopyToClipboard();
    if (type === "Excel") return handleExcelExport();
    if (type === "PDF") return handlePDFExport();

    message.info(`Export to ${type} functionality will be implemented`);
  };

  const generateColumns = () => {
    const baseColumns = [
      {
        title: "#",
        dataIndex: "sno",
        key: "sno",
        sorter: (a, b) => a.sno - b.sno,
        width: 60,
        fixed: "left",
        align: "center",
        render: (text) => <Text strong style={{ color: "#667085" }}>{text}</Text>,
      },
      {
        title: "Status",
        dataIndex: "readyInside",
        key: "readyInside",
        width: 160,
        fixed: "left",
        render: (_, record) => {
          const isDone = record.sorting !== null && record.sorting !== undefined;

          return (
            <Space size={6}>
              <div className={`status-indicator ${isDone ? "prepared" : "pending"}`}>
                {isDone ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                <span>{isDone ? "Prepared" : "Pending"}</span>
              </div>

              {!isDone && (
                <Button
                  size="small"
                  type="text"
                  className="mark-prepared-btn"
                  onClick={async () => {
                    try {
                      const res = await setSortingOne(record.key);
                      if (res?.status) {
                        message.success(res.message || "Sorting updated to 1");
                        setTableData((prev) =>
                          prev.map((r) => (r.key === record.key ? { ...r, sorting: 1 } : r))
                        );
                        await handleLoadKanbanPlan();
                      } else {
                        message.error(res?.message || "Failed to update sorting");
                      }
                    } catch (err) {
                      message.error("Error calling Prepare API");
                      console.error("Prepare API error:", err);
                    }
                  }}
                >
                  Mark prepared
                </Button>
              )}
            </Space>
          );
        },
      },
      {
        title: "Kanban No",
        dataIndex: "kanbanNumber",
        key: "kanbanNumber",
        sorter: (a, b) => String(a.kanbanNumber).localeCompare(String(b.kanbanNumber)),
        width: 130,
        fixed: "left",
        render: (text) => <div className="kanban-badge">{text}</div>,
      },
    ];

    const dynamicSizeColumns = sizeColumns.map((sizeKey) => ({
      title: sizeKey,
      dataIndex: sizeKey,
      key: sizeKey,
      width: 70,
      align: "center",
      render: (text, record) => {
        const hasValue = text !== "" && text !== null && text !== undefined && text !== 0;

        return (
          <div
            className="size-cell"
            style={{
              cursor: hasValue ? "pointer" : "default",
              opacity: hasValue ? 1 : 0.6,
            }}
            onClick={() => {
              if (!hasValue) return;
              handleOpenEditModal(record, sizeKey);
            }}
          >
            {hasValue ? text : "-"}
          </div>
        );
      },
    }));

    const endColumns = [
      {
        title: "Total",
        dataIndex: "total",
        key: "total",
        width: 90,
        align: "center",
        render: (text) => (
          <Text strong className="total-cell">
            {text || "0"}
          </Text>
        ),
      },
      {
        title: "Sorting Date",
        dataIndex: "sortingDateTime",
        key: "sortingDateTime",
        width: 160,
        render: (_, record) => record.sortingDateTime || "-",
      },
    ];

    return [...baseColumns, ...dynamicSizeColumns, ...endColumns];
  };

  const columns = useMemo(() => generateColumns(), [sizeColumns, tableData]);

  const mergedSizes = useMemo(() => {
    if (!poDetails) return [];
    const poSizes = Object.keys(poDetails.kanbanSetSizes || {});
    const shipSizes = Object.keys(poDetails.kanbanSetShipSize || {});
    return Array.from(new Set([...poSizes, ...shipSizes])).sort(sortSizes);
  }, [poDetails]);

  const poRowValues = mergedSizes.map(
    (s) => parseInt(poDetails?.kanbanSetSizes?.[s] ?? 0, 10) || 0
  );
  const cutRowValues = mergedSizes.map(
    (s) => parseInt(poDetails?.kanbanSetShipSize?.[s] ?? 0, 10) || 0
  );
  const poRowTotal = poRowValues.reduce((sum, v) => sum + v, 0);
  const cutRowTotal = cutRowValues.reduce((sum, v) => sum + v, 0);

  return (
    <div className="modern-kanban-container">
      <style>
        {`
           .modern-kanban-container {
      padding: 20px;
      background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
      min-height: 100vh;
    }

    .modern-kanban-container .ant-select .ant-select-arrow,
    .modern-kanban-container .ant-select .ant-select-suffix {
      color: #222222 !important;
    }

    .modern-kanban-container .ant-select .ant-select-arrow .anticon-loading,
    .modern-kanban-container .ant-select .ant-select-arrow .anticon-spin,
    .modern-kanban-container .ant-select .ant-select-suffix .anticon-loading,
    .modern-kanban-container .ant-select .ant-select-suffix .anticon-spin {
      color: #222222 !important;
      font-size: 14px;
    }

          .modern-card {
            border-radius: 16px;
            border: none;
            box-shadow: 0 4px 24px rgba(15, 23, 42, 0.08);
            background: white;
            overflow: hidden;
          }
          .modern-card .ant-card-head {
            border-bottom: 1px solid #f1f5f9;
            background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
          }
          .modern-card .ant-card-body { padding: 20px; }

          .status-indicator {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 4px 8px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 500;
          }
          .status-indicator.prepared {
            background: #ecfdf5;
            color: #065f46;
            border: 1px solid #a7f3d0;
          }
          .status-indicator.pending {
            background: #fef3f2;
            color: #991b1b;
            border: 1px solid #fecaca;
          }

          .kanban-badge {
            background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
            color: white;
            padding: 6px 12px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            text-align: center;
            display: inline-block;
            min-width: 90px;
          }

          .size-cell {
            padding: 8px 4px;
            border-radius: 8px;
            background: #f8fafc;
            transition: all 0.2s ease;
            font-weight: 500;
            color: #475569;
            border: 1px solid transparent;
          }
          .size-cell:hover {
            background: #e0e7ff;
            border-color: #c7d2fe;
            color: #3730a3;
            transform: translateY(-1px);
          }

          .total-cell { color: #7c3aed; font-size: 14px; }
          .mark-prepared-btn { color: #7c3aed; font-size: 11px; padding: 0; height: auto; }

          .modern-table .ant-table-thead > tr > th {
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            border-bottom: 2px solid #e2e8f0;
            font-weight: 600;
            color: #475569;
          }
          .modern-table .ant-table-tbody > tr:hover > td {
            background: #f8fafc !important;
          }

          .filter-tag {
            border-radius: 20px;
            padding: 4px 12px;
            font-size: 12px;
            font-weight: 500;
          }

          .export-btn {
            border-radius: 8px;
            border: 1px solid #e2e8f0;
            background: white;
            color: #475569;
            font-weight: 500;
          }
          .export-btn:hover {
            border-color: #c7d2fe;
            color: #7c3aed;
          }

          .primary-btn {
            background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
            border: none;
            border-radius: 8px;
            font-weight: 500;
            box-shadow: 0 2px 8px rgba(124, 58, 237, 0.2);
          }
          .primary-btn:hover {
            background: linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
          }

          .compact-stats {
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            border-radius: 12px;
            padding: 12px 16px;
            border: 1px solid #e2e8f0;
          }
          .compact-stat-item { text-align: center; padding: 8px 12px; }
          .compact-stat-value { font-size: 18px; font-weight: 700; line-height: 1.2; }
          .compact-stat-label {
            font-size: 11px; color: #64748b; font-weight: 500;
            text-transform: uppercase; letter-spacing: 0.5px;
          }

          .size-summary-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
            background: white;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          .size-summary-table th {
            background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            padding: 8px 6px;
            text-align: center;
            font-weight: 600;
            color: #374151;
            border-bottom: 1px solid #e2e8f0;
          }
          .size-summary-table td {
            padding: 6px 4px;
            text-align: center;
            border-bottom: 1px solid #f1f5f9;
          }
          .size-summary-table tr:last-child td { border-bottom: none; }
          .summary-row-header {
            background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            font-weight: 600;
            text-align: left;
            padding-left: 12px !important;
          }

          @media (max-width: 768px) {
            .modern-kanban-container { padding: 12px; }
            .modern-card .ant-card-body { padding: 16px; }
          }
        `}
      </style>

      <div style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0, color: "#1e293b", fontWeight: 700 }}>
              Kanban Plan Sheet
            </Title>
            <Text style={{ color: "#64748b", fontSize: 14 }}>
              Manage and track your kanban sorting operations
            </Text>
          </Col>
        </Row>
      </div>

      <Card
        className="modern-card"
        style={{ marginBottom: 24 }}
        title={
          <Space>
            <FilterOutlined />
            <Text strong>Search Criteria</Text>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="color" style={{ display: "none" }}>
            <Input />
          </Form.Item>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label="Item Number"
                name="itemNo"
                rules={[{ required: true, message: "Please select Item No" }]}
              >
                <Select
                  placeholder="Select Item No"
                  size="large"
                  value={selectedItem}
                  onChange={handleItemChange}
                  loading={itemLoading}
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  notFoundContent={itemLoading ? dropdownSpinner : "No data"}
                >
                  {itemNumbers.map((item) => (
                    <Option key={item.item_no} value={item.item_no}>
                      {item.item_no}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label="Style Number"
                name="style"
                rules={[{ required: true, message: "Please select Style" }]}
              >
                <Select
                  placeholder="Select Style"
                  size="large"
                  value={selectedStyle}
                  onChange={handleStyleChange}
                  loading={styleLoading}
                  disabled={!selectedItem}
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  notFoundContent={styleLoading ? dropdownSpinner : "No data"}
                >
                  {styleNumbers.map((style) => (
                    <Option key={style} value={style}>
                      {style}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label="PO Number"
                name="po"
                rules={[{ required: true, message: "Please select PO" }]}
              >
                <Select
                  placeholder="Select PO"
                  size="large"
                  value={selectedPO}
                  onChange={handlePOChange}
                  loading={poLoading}
                  disabled={!selectedStyle}
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  notFoundContent={poLoading ? dropdownSpinner : "No data"}
                >
                  {poNumbers.map((po) => (
                    <Option key={po} value={po}>
                      {po}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label="Color"
                name="setId"
                rules={[{ required: true, message: "Please select Color" }]}
              >
                <Select
                  placeholder="Select Color"
                  size="large"
                  loading={colorLoading}
                  disabled={!selectedPO}
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  optionLabelProp="label"
                  notFoundContent={colorLoading ? dropdownSpinner : "No data"}
                  onChange={(setId) => {
                    const row = (colorSetMap || []).find(
                      (item) => String(item.set_id) === String(setId)
                    );

                    const color = row?.color || null;

                    setSelectedSetId(setId || null);
                    setSelectedColor(color);

                    form.setFieldsValue({
                      setId: setId || null,
                      color,
                    });
                  }}
                  onClear={() => {
                    setSelectedSetId(null);
                    setSelectedColor(null);

                    form.setFieldsValue({
                      setId: null,
                      color: null,
                    });
                  }}
                >
                  {(colorSetMap || []).map((item) => {
                    const optionLabel = `${item.color} (${item.country || "-"})`;

                    return (
                      <Option
                        key={item.set_id}
                        value={String(item.set_id)}
                        label={optionLabel}
                      >
                        {optionLabel}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {(selectedItem || selectedStyle || selectedPO || selectedColor) && (
            <div style={{ marginTop: 16 }}>
              <Text strong style={{ color: "#64748b", marginRight: 12 }}>
                Active Filters:
              </Text>
              <Space wrap>
                {selectedItem && (
                  <Tag className="filter-tag" color="blue">
                    Item: {selectedItem}
                  </Tag>
                )}
                {selectedStyle && (
                  <Tag className="filter-tag" color="purple">
                    Style: {selectedStyle}
                  </Tag>
                )}
                {selectedPO && (
                  <Tag className="filter-tag" color="geekblue">
                    PO: {selectedPO}
                  </Tag>
                )}
                {selectedColor && (
                  <Tag className="filter-tag" color="magenta">
                    Color: {selectedColor}
                  </Tag>
                )}
                {selectedSetId && (
                  <Tag className="filter-tag" color="green">
                    Set ID: {selectedSetId}
                  </Tag>
                )}
              </Space>
            </div>
          )}

          <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
              className="export-btn"
              size="large"
            >
              Reset
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleLoadKanbanPlan}
              loading={tableLoading}
              className="primary-btn"
              size="large"
            >
              Load Plan
            </Button>
          </div>
        </Form>
      </Card>

      {poDetails && (
        <Card className="modern-card" style={{ marginBottom: 24 }}>
          {kbTotals && (
            <div className="compact-stats" style={{ marginBottom: 20 }}>
              <Row gutter={[16, 8]} justify="center">
                <Col xs={8} sm={4}>
                  <div className="compact-stat-item">
                    <div className="compact-stat-value" style={{ color: "#475569" }}>
                      {kbTotals.total || 0}
                    </div>
                    <div className="compact-stat-label">Total Kanbans</div>
                  </div>
                </Col>
                <Col xs={8} sm={4}>
                  <div className="compact-stat-item">
                    <div className="compact-stat-value" style={{ color: "#065f46" }}>
                      {kbTotals.prepared || 0}
                    </div>
                    <div className="compact-stat-label">Prepared</div>
                  </div>
                </Col>
                <Col xs={8} sm={4}>
                  <div className="compact-stat-item">
                    <div className="compact-stat-value" style={{ color: "#991b1b" }}>
                      {kbTotals.pending || 0}
                    </div>
                    <div className="compact-stat-label">Pending</div>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          <div style={{ marginBottom: 24 }}>
            <Title level={5} style={{ color: "#1e293b", marginBottom: 16 }}>
              PO Information
            </Title>
            <Row gutter={[16, 8]} style={{ alignItems: "center" }}>
              <Col flex="1">
                <div style={{ textAlign: "center" }}>
                  <Text strong style={{ color: "#64748b", display: "block", fontSize: 12 }}>
                    PO Number
                  </Text>
                  <Text style={{ color: "#1e293b", fontSize: 14 }}>{poDetails.poNo}</Text>
                </div>
              </Col>
              <Col flex="1">
                <div style={{ textAlign: "center" }}>
                  <Text strong style={{ color: "#64748b", display: "block", fontSize: 12 }}>
                    Customer
                  </Text>
                  <Text style={{ color: "#1e293b", fontSize: 14 }}>{poDetails.customer}</Text>
                </div>
              </Col>
              <Col flex="1">
                <div style={{ textAlign: "center" }}>
                  <Text strong style={{ color: "#64748b", display: "block", fontSize: 12 }}>
                    Country
                  </Text>
                  <Text style={{ color: "#1e293b", fontSize: 14 }}>{poDetails.country}</Text>
                </div>
              </Col>
              <Col flex="1">
                <div style={{ textAlign: "center" }}>
                  <Text strong style={{ color: "#64748b", display: "block", fontSize: 12 }}>
                    Ship Date
                  </Text>
                  <Text style={{ color: "#1e293b", fontSize: 14 }}>{poDetails.shipDate}</Text>
                </div>
              </Col>
              <Col flex="1">
                <div style={{ textAlign: "center" }}>
                  <Text strong style={{ color: "#64748b", display: "block", fontSize: 12 }}>
                    PO Date
                  </Text>
                  <Text style={{ color: "#1e293b", fontSize: 14 }}>{poDetails.poDate}</Text>
                </div>
              </Col>
              <Col flex="1">
                <div style={{ textAlign: "center" }}>
                  <Text strong style={{ color: "#64748b", display: "block", fontSize: 12 }}>
                    Carton Pack Type
                  </Text>
                  <Text style={{ color: "#1e293b", fontSize: 14 }}>
                    {poDetails.cartonPackType}
                  </Text>
                </div>
              </Col>
            </Row>
          </div>

          <div>
            <Title level={5} style={{ color: "#1e293b", marginBottom: 16 }}>
              Size Summary
            </Title>
            <div style={{ overflowX: "auto" }}>
              <table className="size-summary-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", paddingLeft: "12px", minWidth: "100px" }}>
                      Size
                    </th>
                    {mergedSizes.map((size) => (
                      <th key={size} style={{ minWidth: "50px" }}>
                        {size}
                      </th>
                    ))}
                    <th style={{ minWidth: "60px" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="summary-row-header">Order Qty</td>
                    {mergedSizes.map((size) => {
                      const value = parseInt(poDetails?.orderSizes?.[size] ?? 0, 10) || 0;
                      return <td key={`order-${size}`}>{value || "-"}</td>;
                    })}
                    <td style={{ fontWeight: 600, background: "#f8fafc" }}>
                      {mergedSizes.reduce(
                        (sum, size) =>
                          sum + (parseInt(poDetails?.orderSizes?.[size] ?? 0, 10) || 0),
                        0
                      )}
                    </td>
                  </tr>

                  <tr>
                    <td className="summary-row-header">PO Qty</td>
                    {poRowValues.map((value, index) => (
                      <td key={`po-${index}`}>{value || "-"}</td>
                    ))}
                    <td style={{ fontWeight: 600, background: "#f8fafc" }}>{poRowTotal}</td>
                  </tr>

                  <tr>
                    <td className="summary-row-header">Cut Qty</td>
                    {cutRowValues.map((value, index) => (
                      <td key={`cut-${index}`}>{value || "-"}</td>
                    ))}
                    <td style={{ fontWeight: 600, background: "#f8fafc" }}>{cutRowTotal}</td>
                  </tr>

                  <tr>
                    <td className="summary-row-header">Ship %</td>
                    {mergedSizes.map((size) => (
                      <td key={`ship-${size}`}>{poDetails?.shipPercentage?.[size] || "0%"}</td>
                    ))}
                    <td style={{ fontWeight: 600, background: "#f8fafc" }}>
                      {poDetails?.shipPercentage?.["Total %"] || "0%"}
                    </td>
                  </tr>

                  <tr>
                    <td className="summary-row-header">Prepared Qty</td>
                    {mergedSizes.map((size) => {
                      const value = parseInt(preparedSizeTotals?.[size] ?? 0, 10) || 0;
                      return <td key={`prepared-${size}`}>{value || "-"}</td>;
                    })}
                    <td style={{ fontWeight: 600, background: "#f8fafc" }}>
                      {mergedSizes.reduce(
                        (sum, size) =>
                          sum + (parseInt(preparedSizeTotals?.[size] ?? 0, 10) || 0),
                        0
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}

      <Card
        className="modern-card"
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <Text strong>Kanban Records</Text>
            </Col>
            <Col>
              <Space>
                <Input
                  placeholder="Search records..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{ width: screens.xs ? 150 : 200 }}
                  allowClear
                />
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => handleExport("Copy")}
                  className="export-btn"
                >
                  {screens.xs ? "" : "Copy"}
                </Button>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={() => handleExport("Excel")}
                  className="export-btn"
                >
                  {screens.xs ? "" : "Excel"}
                </Button>
                <Button
                  icon={<FilePdfOutlined />}
                  onClick={() => handleExport("PDF")}
                  className="export-btn"
                >
                  {screens.xs ? "" : "PDF"}
                </Button>
              </Space>
            </Col>
          </Row>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={tableLoading}
          pagination={false}
          scroll={{ x: "max-content", y: tableY }}
          size="middle"
          className="modern-table"
        />
      </Card>

      <Modal
        title={
          <Space>
            <EditOutlined />
            <span>
              Edit <b>{editingSizeKey}</b> Quantity
              {editingRecord?.kanbanNumber && (
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  - {editingRecord.kanbanNumber}
                </Text>
              )}
            </span>
          </Space>
        }
        open={editModalVisible}
        onCancel={handleCloseEditModal}
        onOk={handleSaveSizeQuantity}
        confirmLoading={loading}
        width={400}
        okText="Save Changes"
        cancelText="Cancel"
        okButtonProps={{
          style: { display: editingRecord?._isLocked ? "none" : "inline-block" },
          className: "primary-btn",
        }}
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label={`${editingSizeKey} Quantity`}
            name={editingSizeKey}
            rules={[
              { required: true, message: "Please enter a quantity" },
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === "") {
                    return Promise.resolve();
                  }
                  if (isNaN(value) || value < 0) {
                    return Promise.reject(new Error("Must be a positive number"));
                  }
                  if (value > 9999) {
                    return Promise.reject(new Error("Quantity too large"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              min={0}
              max={9999}
              style={{ width: "100%" }}
              size="large"
              placeholder="0"
              precision={0}
              autoFocus
              disabled={editingRecord?._isLocked}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(value) => String(value).replace(/\$\s?|(,*)/g, "")}
            />
          </Form.Item>

          {editingRecord && editingRecord[editingSizeKey] !== undefined && (
            <div
              style={{
                background: "#f0f8ff",
                padding: 8,
                borderRadius: 6,
                marginBottom: 16,
              }}
            >
              <Text type="secondary" style={{ fontSize: 12 }}>
                Current quantity: <b>{editingRecord[editingSizeKey]}</b>
              </Text>
            </div>
          )}

         {editingRecord?._isLocked && (
  <div
    style={{
      background: "#fef3f2",
      padding: 12,
      borderRadius: 8,
      border: "1px solid #fecaca",
    }}
  >
    <Alert
      message="Locked Kanban"
      description={`${editingRecord?._lockMessage}. Quantity cannot be edited.`}
      type="warning"
      showIcon
    />
  </div>
)}
        </Form>
      </Modal>
    </div>
  );
};

export default KanbanPlanSheetPage;